import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import UploadField from '@/components/UI/UploadField';
import Breadcrumb from '@/components/common/Breadcrumb';
import MediaInputField from '@/components/common/media/MediaInputField';
import MediaPreview from '@/components/common/media/MediaPreview';
import { IMediaProp } from '@/interfaces/mediaResponse.interface';
import { RoutesLink } from '@/utility/RouteLinks';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorMessage from '../../components/common/ErrorMessage';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import ProductDragDrop from '@/components/product/ProductDragDrop';
import { IActiveDiscount } from '@/interfaces/discount.interface';
import BrandDiscount from '@/components/category_brands/BrandDiscount';
import Modal from '@/components/common/models/Modal';

export interface IBrand {
  name: string;
  description: string;
  status: 'active' | 'inactive';
  file: File | null;
  activeDiscount: IActiveDiscount | null;
}

const EditBrand = () => {
  const navigate = useNavigate();
  const [mediaFile, setMediaFile] = useState<IMediaProp | null>(null);
  const { id } = useParams();
  const brandId = id;
  const [isLoading, setIsLoading] = useState(false);
  const [isInfoLoading, setIsInfoLoading] = useState(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<IBrand>({
    defaultValues: {
      name: '',
      description: '',
      status: 'active',
      file: null,
    },
  });

  const onSubmit = async (data: IBrand) => {
    try {
      setIsLoading(true);

      let PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/${brandId}`;
      await callApi(PATH, {
        method: 'PATCH',
        multipart: true,
        data,
      });
      setIsLoading(false);
      navigate(RoutesLink.brandsList);
    } catch (error) {
      setIsLoading(false);
      ThrowError(error);
    }
  };

  const onSaveInfo = async (data: IBrand) => {
    try {
      setIsInfoLoading(true);

      const { activeDiscount, ...brandData } = data;

      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/${brandId}`;

      await callApi(PATH, {
        method: 'PATCH',
        multipart: true,
        data: brandData,
      });
      navigate(RoutesLink.brandsList);
      setIsInfoLoading(false);
    } catch (error) {
      setIsInfoLoading(false);
      ThrowError(error);
    }
  };

  const handleDeleteMedia = async (id?: string) => {
    if (id) {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.media}/${id}`;
      const res = await callApi(PATH, { method: 'DELETE' });
      if (res) {
        setMediaFile(null);
      }
    } else {
      setMediaFile(null);
    }
  };

  const fetchBrandInfo = async () => {
    try {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/${brandId}`;
      const res: any = await callApi(PATH, { method: 'GET' });
      reset({
        name: res.name,
        description: res.description,
        status: res.status,
        file: null,
        activeDiscount: res.activeDiscount,
      });

      if (res.media && res.media.length > 0) {
        setMediaFile({
          id: res.media[0].id,
          url: brandId
            ? res.media[0].url
            : `${API_URL_PRODUCT_SERVICE}/${res.media[0].url}`,
          type: res.media[0].type,
          altText: res.media[0].altText || 'Brand Logo',
        });
      }
    } catch (err) {
      ThrowError(err);
    }
  };

  useEffect(() => {
    brandId && fetchBrandInfo();
  }, [brandId, reset]);

  const breadcrumb = [
    { label: 'Brands', href: RoutesLink.brandsList },
    { label: 'Edit Brand' },
  ];

  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="space-y-5">
            {/* About Brand */}
            <h2 className="text-lg font-semibold mb-4">About Brand</h2>
            <div>
              <FormField
                label="Name"
                type="text"
                placeholder="Enter brand name"
                {...register('name')}
              />
            </div>

            <div>
              <FormField
                type="textarea"
                label="Description"
                placeholder="Start describing the brand..."
                rows={4}
                {...register('description', {
                  required: 'Description is required',
                })}
              />
              <ErrorMessage message={errors.description?.message} />
            </div>

            <div>
              <h4 className="block text-sm font-medium text-gray-700 mb-2">
                Upload Brand Logo
              </h4>

              {brandId ? (
                <UploadField
                  referenceType="brand"
                  referenceId={brandId}
                  multiple={false}
                  initialMedia={mediaFile ? [mediaFile] : []}
                />
              ) : (
                <Controller
                  name="file"
                  control={control}
                  render={({ field }) => (
                    <MediaInputField
                      helperText="Upload brand logo"
                      multiple={false}
                      onFilesChange={(files) => {
                        if (files && files.length > 0) {
                          const file = files[0];
                          field.onChange(file);
                          setMediaFile({
                            url: URL.createObjectURL(file),
                            type: file.type.startsWith('video/')
                              ? 'video'
                              : 'image',
                            altText: file.name,
                          });
                        }
                      }}
                    />
                  )}
                />
              )}
              {mediaFile && !brandId && (
                <div className="mt-4">
                  <MediaPreview
                    mediaUrls={[mediaFile]}
                    handleDeleteMedia={handleDeleteMedia}
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
        <div className="flex justify-end gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            type="button"
            disabled={isInfoLoading}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            disabled={isInfoLoading}
            type="button"
            className="btn-primary"
            onClick={handleSubmit(onSaveInfo)}
          >
            {isInfoLoading && <BtnLoader />} Save Info
          </button>
        </div>
        <BrandDiscount
          register={register}
          watch={watch}
          setValue={setValue}
          control={control}
          error={errors}
        />

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            type="button"
            disabled={isLoading}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            disabled={isLoading}
            type="button"
            onClick={() => setShowPopup(true)}
            className="btn-primary"
          >
            {isLoading && <BtnLoader />} Update Discount
          </button>
        </div>
        <Modal
          show={showPopup}
          onClose={() => setShowPopup(false)}
          title="Save Changes"
          size="w-[500px] top-1/2! -translate-y-1/2"
          height="max-h-70"
        >
          <div className="w-full">
            <p className="mb-5 text-sm text-gray-600">
              Are you sure you want to continue?
            </p>
            <div className="mb-6 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <svg
                  className="h-4 w-4 text-amber-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v4m0 4h.01M10.29 3.86l-8.18 14a2 2 0 001.73 3h16.32a2 2 0 001.73-3l-8.18-14a2 2 0 00-3.42 0z"
                  />
                </svg>
              </div>

              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  Updating discount on brand will apply discount on all
                </p>

                <p className="mt-1 text-sm leading-5 text-amber-900">
                  products under this brand and will remove any existing product
                  level discounts
                </p>
              </div>
            </div>

            {/* Confirmation */}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowPopup(false)}
                className="btn btn-secondary min-w-[110px]"
              >
                Cancel
              </button>

              <button
                disabled={isLoading || isInfoLoading}
                type="submit"
                className="btn btn-primary min-w-[110px]"
              >
                {isLoading && <BtnLoader />}
                {!isLoading && 'Update'}
              </button>
            </div>
          </div>
        </Modal>
      </form>
      <ProductDragDrop brandId={brandId as string} />
    </Layout>
  );
};

export default EditBrand;
